import { handleActions } from 'redux-actions';
import moment from 'moment';

import _omit from 'lodash/omit';
import _sumBy from 'lodash/sumBy';

import createCalendar, { getDateRangeWithTimeZoneFromCalendar } from '../../shared/calendar';
import createFilters, { getFiltersObject } from '../../shared/filters';
import { createSelectOptionsFromSysEnums } from '../../shared/form-elements/select';
import http from '../../shared/http';

import endpoints from '../../../config/endpoints';
import csvExport from '../../shared/csvExport';

export const namespace = 'marketingFunnelRegistrationActivity';

const formCalendar = createCalendar({
	namespace,
	stateNodeName: 'formCalendar',
	defaultValue: {
		start: moment().startOf('isoWeek').format('YYYY-MM-DD'),
		end: moment().endOf('isoWeek').format('YYYY-MM-DD')
	}
});

const calendar = createCalendar({
	namespace,
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(3, 'month').startOf('month').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace,
	stateNodeName: 'filters',
	listOfFilters: [
		{
			name: 'levels',
			title: 'Levels',
			placeholder: 'Add levels',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'static',
				source: 'root.lists.affiliate-levels'
			}
		},
		{
			name: 'utm_source',
			title: 'Utm source',
			placeholder: 'Add utm source',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.UTM_SOURCE
			}
		},
		{
			name: 'utm_medium',
			title: 'Utm medium',
			placeholder: 'Add utm medium',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.UTM_MEDIUM
			}
		}
	]
});

// action types
const FETCH_DATA_SUCCESS = `${namespace}/fetch-data-success`;
const FETCH_EXPANDED_SUCCESS = `${namespace}/fetch-expanded-success`;
const CLEAR_EXPANDED= `${namespace}/CLEAR_EXPANDED`;
const CHANGE_IS_EMPTY_ONLY = `${namespace}/CHANGE_IS_EMPTY_ONLY`;//isNonEmptyOnly
const CLEAR_DATA = `${namespace}/CLEAR_DATA`;
const FETCH_DATA_ERROR = `${namespace}/fetch-data-error`;
const TOGGLE_PROGRESS_BAR = `${namespace}/toggle-progress-bar`;
const APPLY = `${namespace}/apply`;
const CLEAR = `${namespace}/clear`;

const SET_INITIAL_VALUES = `${namespace}/set-initial-values`;
const CHANGE_UTM = `${namespace}/CHANGE_UTM`;
const CHANGE_SORT = `${namespace}/CHANGE_SORT`;
const TOGGLE_EXPANDED_LOADER = `${namespace}/TOGGLE_EXPANDED_LOADER`;

// initial state
const initialState = {
	destroyOnUnmount: true,
	isLoading: true,
	isError: false,
	data: null,
	affiliatesLoadingFromId: null,
	utm: 'utm_source',
	expandedData: {},
	sort: {},
	isNonEmptyOnly: true,
	form: null,
	formCalendar: formCalendar.initialState,
	filters: filters.initialState,
	calendar: calendar.initialState
};

// actions
export const actions = {
	own: {
		fetchData: () => (dispatch, getState) => {
			request(getRequestQuery(getState), FETCH_DATA_SUCCESS)(dispatch);
		},
		exportCsv: () => (dispatch, getState) => {
			const rows = selectors.getRows(getState()) || [];
			const headers = Object.keys(rows[0]);

			const columns = rows.map(entity => headers.map(h => entity[h] || '-'));
			const rowsForExport = [].concat([ headers ], columns);

			return csvExport('registration-activity-report', rowsForExport);
		},
		fetchExpandedData: ({ row_id, affiliate_ids }) => (dispatch, getState) => {
			if (getState()[namespace].expandedData[row_id]) {
				return dispatch({ type: CLEAR_EXPANDED, payload: row_id});
			}
			dispatch({ type: TOGGLE_EXPANDED_LOADER, payload: row_id});

			return http({
				url: 'v2/marketing-funnel/expanded-registration-activity',
				method: 'post',
				actions: [ FETCH_EXPANDED_SUCCESS, FETCH_DATA_ERROR],
				addToResponse: row_id,

				data: { ids: affiliate_ids },
				query: { row_id, ...getRequestQuery(getState) }
			})(dispatch);
		},
		changeUtm: id => dispatch => {
			dispatch({ type: CHANGE_UTM, payload: id });
			dispatch({ type: CLEAR_DATA });
			dispatch({ type: TOGGLE_PROGRESS_BAR });
			dispatch(actions.own.fetchData());
		},
		changeSort: (name) => (dispatch, getState) => {
			const sort = selectors.getSort(getState());
			dispatch({ type: CHANGE_SORT, payload: {
				name: name,
				direction: (sort.name !== name || sort.direction === 'desc')  ? 'asc' : 'desc'
			}});
			dispatch({ type: APPLY });

			dispatch(actions.own.fetchData());
		},
		toggleProgressBar: () => ({ type: TOGGLE_PROGRESS_BAR }),
		changeIsEmptyOnly: (val) => dispatch => {
			dispatch({ type: CHANGE_IS_EMPTY_ONLY, payload: val });
			dispatch({ type: TOGGLE_PROGRESS_BAR });
			dispatch(actions.own.fetchData());
		}
	},
	apply: () => (dispatch, getState) => {
		dispatch({ type: APPLY });
		dispatch(actions.own.fetchData());
	},
	clear: () => (dispatch, getState) => {
		dispatch({ type: CLEAR });
		dispatch(actions.own.fetchData());
	},
	formCalendar: formCalendar.actions,
	filters: filters.actions,
	calendar: calendar.actions
};

const reducers = Object.assign(
	{
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: action.payload.data.data,
			intervals: action.payload.data.intervals,
			isLoading: false,
			isError: false
		}),
		[FETCH_EXPANDED_SUCCESS]: (state, action) => ({
			...state,
			affiliatesLoadingFromId: null,
			expandedData: {...state.expandedData, [action.meta]: action.payload.data.data }
		}),
		[CLEAR_EXPANDED]: (state, action) => ({
			...state,
			expandedData: {..._omit(state.expandedData, [action.payload]) }
		}),
		[CHANGE_IS_EMPTY_ONLY]: (state, action) => ({
			...state,
			isNonEmptyOnly: action.payload
		}),
		[TOGGLE_EXPANDED_LOADER]: (state, action) => ({
			...state,
			affiliatesLoadingFromId: action.payload
		}),
		[CLEAR_DATA]: (state, action) => ({
			...state,
			expandedData: {},
			data: null
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({
			...state,
			destroyOnUnmount: false,
			isLoading: false,
			isError: true
		}),
		[TOGGLE_PROGRESS_BAR]: (state, action) => ({
			...state,
			isLoading: !state.isLoading
		}),
		[CHANGE_UTM]: (state, action) => ({
			...state,
			utm: action.payload
		}),
		[CHANGE_SORT]: (state, action) => ({
			...state,
			sort: {...action.payload}
		}),
		[APPLY]: (state, action) => ({
			...state,
			data: null,
			expandedData: {},
			isLoading: true
		}),
		[CLEAR]: (state, action) => initialState,

		[SET_INITIAL_VALUES]: (state, action) => ({
			...state,
			destroyOnUnmount: true,
			form: null,
			isLoading: false,
			isError: false
		})
	},
	formCalendar.reducers,
	filters.reducers,
	calendar.reducers
);

// reducers
export default handleActions(reducers, initialState);

// selectors
export const selectors = {
	select: (state, field) => state[namespace][field],
	getSort: state => state[namespace].sort,
	getTotalAffsCount: state => {
		const data = state[namespace].data || [];

		return _sumBy(data, item => Number(
			!item.utm_value && !!item.created_month && item.affiliate_ids ? item.affiliate_ids.length : 0
		));
	},
	getAffiliatesLoadingFromId: state => state[namespace].affiliatesLoadingFromId,
	getIsNonEmptyOnly: state => state[namespace].isNonEmptyOnly,
	getRows: (state) => {
		const data = state[namespace].data || [];
		const expandedData = state[namespace].expandedData;

		return [...data]
			.reduce((acc, val) => {

				if (expandedData[val.row_id] && expandedData[val.row_id].length) {
					const expandedRows = expandedData[val.row_id];
					return [...acc, val, ...expandedRows];
				}

				return [...acc, val];
			}, []);
	},
	getInitialValues: state => {
		const form = selectors.select(state, 'form');

		return form && {
			...form,
			type: [{ value: form.type, label: form.type }],
			tag: [{ value: form.tag, label: form.tag }]
		};
	},
	getUtms: state => {
		return state.root.lists['utm-tags']
			.filter(item => item.id.startsWith('utm'))
			.map(item => ({ name: item.id, placeholder: item.id, active: item.id ===  state[namespace].utm }));
	},
	getFormCalendar: state => formCalendar.selectors.getCalendar(
		state => state[namespace].formCalendar,
		state => state
	)(state),
	getFilters: state => filters.selectors.getFilters(
		state => state[namespace].filters,
		state => state
	)(state),
	getCalendar: state => calendar.selectors.getCalendar(
		state => state[namespace].calendar,
		state => state
	)(state),
	getActivityTypes: state => createSelectOptionsFromSysEnums(state.root.lists['marketing-funnel-activity-types'])
};

function getRequestQuery(getState) {
	const state = getState()[namespace];
	const sort = selectors.getSort(getState());

	const range = getDateRangeWithTimeZoneFromCalendar(state.calendar);
	const filters = getFiltersObject(state.filters, false);
	const inverse_filters = getFiltersObject(state.filters, true);

	const isNonEmptyOnly = state.isNonEmptyOnly;

	return {
		dates: [
			moment.utc(range.start)
				.hours(0).minutes(0).seconds(0).milliseconds(0)
				.format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
			moment.utc(range.end)
				.hours(23).minutes(59).seconds(59).milliseconds(999)
				.format('YYYY-MM-DDTHH:mm:ss.SSSZ')
		],
		isNonEmptyOnly: isNonEmptyOnly || '',
		utm: getState()[namespace].utm,
		filters,
		sort,
		inverse_filters
	};
}

function request(query, successActionType) {
	return http({
		url: 'v2/marketing-funnel/registration-activity',
		method: 'get',
		actions: [successActionType, FETCH_DATA_ERROR],
		query
	});
}



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/marketing-funnel/registration-activity/index.js